import PropTypes from "prop-types";
import { Field, Form, Formik } from "formik";
import { FormattedMessage } from "react-intl";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import FacebookContainer from "app/pages/Auth/Facebook/FacebookContainer.jsx";
import Popover from "app/components/ui/Popover/Popover";
import PasswordPolicyValidation from "app/pages/Auth/Signup/PasswordPolicyValidation";
import { ALERT_TYPE, PASSWORD_LIMIT_CHARS, PASSWORD_POLICY_REGEX, RESOLUTION } from "app/constants";
import AlertMessage from "app/pages/.shared/AlertMessage";
import CheckboxFormik from "app/pages/.shared/form/CheckboxFormik";
import InputFormik from "app/pages/.shared/form/InputFormik";
import FormErrorMessages from "app/pages/.shared/form/FormErrorMessages/FormErrorMessages";
import { boolean, object, string } from "yup";
import { sendTagOnEmailSignupError } from "app/utils/analytics";
import useShowPassword from "app/utils/hooks/useShowPassword";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useCallback, useContext, useRef, useState } from "react";
import SignInWithGoogleButtonContainer from "app/pages/Auth/Google/SignInWithGoogleButtonContainer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import {
	arrow,
	flip,
	FloatingArrow,
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
} from "@floating-ui/react";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS } from "app/constants";
import { InfoCircleStrokeIcon } from "app/icons/navigation";
import "./Signup.scss";

const validationSchema = object().shape({
	email: string()
		.required({ id: "error.email.required" })
		.email({ id: "error.email.format" }),
	password: string()
		.min(PASSWORD_LIMIT_CHARS, { id: "auth.password.policy.not.valid" })
		.required({ id: "error.password.required" })
		.test(
			"atLeastOneUppercase",
			{ id: "auth.password.policy.not.valid" },
			value => value && value.length > 0 && PASSWORD_POLICY_REGEX.UPPERCASE.test(value)
		)
		.test(
			"atLeastOneDigit",
			{ id: "auth.password.policy.not.valid" },
			value => value && value.length > 0 && PASSWORD_POLICY_REGEX.DIGIT.test(value)
		)
		.test(
			"atLeastOneSpecialChar",
			{ id: "auth.password.policy.not.valid" },
			value => value && value.length > 0 && PASSWORD_POLICY_REGEX.SPECIAL_CHAR.test(value)
		),
	terms: boolean()
		.required({ id: "error.must.accept.terms" })
		.oneOf([true], { id: "error.must.accept.terms" }),
});

const Signup = ({
	handleSubmit,
	showTopFacebook = true,
	facebookOnSuccess,
	cnilDocumentName,
	confidentialiteDocumentName,
	brandDisplayName,
	signupButtonLabel = <FormattedMessage id="auth.sign.up.button.label" />,
	footer = null,
}) => {
	const { resolution, brand } = useContext(AppGlobalsContext);
	const isBrandAF = brand === BRANDS.AF;

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const [icon, showPassword] = useShowPassword();
	const { track } = useAnalytics();

	const handleCheckClick = useCallback(() => {
		track("signup_click_email");
	}, []);

	const arrowRef = useRef(null);

	const [openDropdownMenu, toggleDropdownMenu] = useState(false);

	const { context, x, y, refs, strategy } = useFloating({
		placement: isMobile ? "top" : "left",
		strategy: "absolute",
		open: openDropdownMenu,
		onOpenChange: toggleDropdownMenu,
		middleware: [
			offset(10),
			shift(),
			flip(),
			arrow({
				element: arrowRef,
			}),
		],
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

	return (
		<div className="signup">
			<div className="signup__main">
				<Formik
					initialValues={{
						email: "",
						password: "",
						terms: false,
						...(isBrandAF && { newsletter: false }),
					}}
					onSubmit={handleSubmit}
					validateOnChange={false}
					validateOnBlur={false}
					validationSchema={validationSchema}
				>
					{({
						errors = {},
						values = {},
						isSubmitting,
						setErrors,
						setSubmitting,
						setStatus,
						submitCount,
					}) => {
						return (
							<Form
								noValidate
								className="signup__form"
								onChange={() => {
									setStatus({});
								}}
							>
								<FormErrorMessages
									errors={errors}
									isSubmitting={isSubmitting}
									submitCount={submitCount}
									onError={() =>
										sendTagOnEmailSignupError({
											status: "",
											error: errors[Object.keys(errors)[0]]?.id,
										})
									}
								/>

								<div className="signup__form-inputs">
									<InputFormik
										type="email"
										name="email"
										id="auth-email"
										data-testid="email-input"
										label={<FormattedMessage id="auth.email.label" />}
									/>

									<div ref={refs.setReference} {...getReferenceProps({})}>
										<InputFormik
											type={showPassword ? "text" : "password"}
											name="password"
											id="auth-password"
											data-testid="password-input"
											label={<FormattedMessage id="auth.password.label" />}
											className="signup__password"
											note={icon}
										/>
									</div>

									<FloatingPortal>
										{openDropdownMenu && values.password && (
											<div
												ref={refs.setFloating}
												className="signup__dropdown"
												{...getFloatingProps({
													style: {
														position: strategy,
														left: x ?? "",
														top: y ?? "",
													},
												})}
											>
												<FloatingArrow
													ref={arrowRef}
													context={context}
													fill="white"
													stroke="white"
													strokeWidth={1}
													className="signup__dropdown-arrow"
												/>
												<PasswordPolicyValidation value={values.password} />
											</div>
										)}
									</FloatingPortal>
								</div>

								<div className="signup__accept-cgv">
									{isBrandAF ? (
										<>
											<Field
												component={CheckboxFormik}
												name="terms"
												id="accept-terms"
												grouped={false}
											>
												<Typography
													variant={TYPOGRAPHY_VARIANTS.SMALL}
													className="signup__terms"
													data-testid="auth-terms"
												>
													<FormattedMessage
														id="auth.terms.label"
														values={{
															cguLink: (
																<a
																	className="relative-link"
																	rel="noopener noreferrer"
																	target="_blank"
																	href={
																		confidentialiteDocumentName
																	}
																>
																	<FormattedMessage id="auth.terms.label.cgu.link.label" />
																</a>
															),
														}}
													/>
												</Typography>
											</Field>
											<Popover dataTestId="signup-terms-info">
												<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR}>
													<FormattedMessage
														id="auth.terms.info.tooltip"
														values={{
															confidentialiteLink: (
																<a
																	rel="noopener noreferrer"
																	target="_blank"
																	href={
																		confidentialiteDocumentName
																	}
																>
																	<FormattedMessage id="auth.terms.info.tooltip.link.label" />
																</a>
															),
														}}
													/>
												</Typography>
											</Popover>
										</>
									) : (
										<>
											<Field
												component={CheckboxFormik}
												name="terms"
												id="accept-terms"
												grouped={false}
											>
												<Typography
													variant={TYPOGRAPHY_VARIANTS.SMALL}
													className="signup__terms"
													data-testid="auth-terms"
												>
													<FormattedMessage
														id="auth.terms.label"
														values={{
															cguLink: (
																<a
																	className="relative-link"
																	rel="noopener noreferrer"
																	target="_blank"
																	href={
																		confidentialiteDocumentName
																	}
																>
																	<FormattedMessage id="auth.terms.label.cgu.link.label" />
																</a>
															),
															confidentialiteLink: (
																<a
																	className="relative-link"
																	rel="noopener noreferrer"
																	target="_blank"
																	href={
																		confidentialiteDocumentName
																	}
																>
																	<FormattedMessage id="auth.terms.label.confidentialite.link.label" />
																</a>
															),
														}}
													/>
												</Typography>
											</Field>
											{cnilDocumentName && (
												<a
													rel="noopener noreferrer"
													target="_blank"
													href={cnilDocumentName}
													data-testid="cnil-document"
													className="signup__cnil-document"
												>
													<InfoCircleStrokeIcon width={12} height={12} />
												</a>
											)}
										</>
									)}
								</div>
								{isBrandAF && (
									<div className="signup__newsletter">
										<Field
											component={CheckboxFormik}
											name="newsletter"
											id="accept-newsletter"
											data-testid="auth-newsletter"
											grouped={false}
										>
											<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
												<FormattedMessage
													id="auth.newsletter.label"
													values={{
														brandName: brandDisplayName,
													}}
												/>
											</Typography>
										</Field>
									</div>
								)}
								<div className="signup__action">
									{errors._error && (
										<AlertMessage
											alertType={ALERT_TYPE.ERROR}
											message={<FormattedMessage id={errors._error.id} />}
										/>
									)}
									<Button
										className="signup__button"
										submit
										variant={BUTTON_VARIANTS.FILLED}
										size={BUTTON_SIZES.LARGE}
										loading={isSubmitting}
										data-testid="signup-button"
										onClick={handleCheckClick}
									>
										{signupButtonLabel}
									</Button>
								</div>

								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									className="signup__separator"
								>
									<FormattedMessage id="general.ou" />
								</Typography>

								<div className="signup__social-buttons">
									<div className="signup__google">
										<SignInWithGoogleButtonContainer
											uxMode={"signup"}
											onAuthSuccess={facebookOnSuccess}
											onAuthError={() => {
												setErrors({
													_error: {
														id: "error.generic",
													},
												});
												setSubmitting(false);
											}}
										/>
									</div>

									{showTopFacebook && (
										<div
											className="signup__facebook"
											data-testid="auth-facebook-top"
										>
											<FacebookContainer
												signup
												onSuccess={facebookOnSuccess}
												onError={() => {
													setErrors({
														_error: {
															id: "error.generic",
														},
													});
													setSubmitting(false);
												}}
											/>
										</div>
									)}
								</div>
							</Form>
						);
					}}
				</Formik>
				{footer && footer.props?.children && <div className="signup__footer">{footer}</div>}
			</div>
		</div>
	);
};

Signup.propTypes = {
	signupButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
	footer: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
	showTopFacebook: PropTypes.bool,
	cnilDocumentName: PropTypes.string,
	confidentialiteDocumentName: PropTypes.string,
	brandDisplayName: PropTypes.string,
	facebookOnSuccess: PropTypes.func,
	handleSubmit: PropTypes.func,
};

export default Signup;
